import {query} from "@/graphql/client";
import {MonthKpi} from "@/graphql/generated/graphql";
import {kapitelDateString, kapitelDateTimeString, kapitelMonthString, kapitelTimeString} from "@/graphql/kapitelTypes";
import month from "@/helper/datetime/month";
import datetime from "@/helper/datetime/datetime";
import date from "@/helper/datetime/date";
import {graphql} from "@/graphql/generated";
import {useAuthStore} from "@/store/auth";
import {ApexMonthRanger, ApexMonthRangerOptions} from "@/helper/apexMonthRanger";

export const MONTH_KPI_MONTH_BACK = 6;
export const SHARE_PROFIT_DISTRIBUTION_EFFECTIVE_START_DATE = "2025-01-01";
export const PUBLISH_15 = 15;

/**
 * PRODUCTIVITY
 */
export const monthKpiFragment = graphql(`fragment MonthKpiFragment on MonthKPI {
    month
    
    # type of productivity value
    accountedForAbsences
    productivityScope
    
    # productivity value
    productivity
}`)


export async function fetchMonthKPI(month: kapitelDateString): Promise<MonthKpi> {
    const result = await query(
        graphql(`
            query getMonthKPI($month: KapitelDateImmutable!) {            
            monthKPI(month: $month) {
                ...MonthKpiFragment
            }
        }`),
        {month}
    )

    return result.data.monthKPI
}


export async function fetchMonthKPIList(months: kapitelDateString[]): Promise<MonthKpi[]> {
    const result = await query(
        graphql(`
            query getMonthKPIList($months: [KapitelDateImmutable!]!) {            
            monthKPIList(months: $months) {
                ...MonthKpiFragment
            }
        }`),
        {months}
    )

    return result.data.monthKPIList
}





export const getPublishTimeForMonth = (m: kapitelMonthString) : kapitelDateTimeString => {
        return datetime.convertDateAndTime2DateTime(
            date.setDayOfMonth(
                month.add(m, 1),
                PUBLISH_15
            ),
            PUBLISH_15.toString()+':'+PUBLISH_15.toString() as kapitelTimeString
        )
}
export const getMostRecentPublishedMonth = () : kapitelMonthString => getMostRecentPublishedMonthForPIT(datetime.getNow())
export const isMostRecentPublishedMonth = (month: kapitelMonthString) => month === getMostRecentPublishedMonth()
export const getMostRecentPublishedMonthForPIT = (pit: kapitelDateTimeString) : kapitelMonthString =>  {
    const pitMonth = month.getKapitelMonthFromKapitelDate(datetime.convertDateTime2Date(pit))
    const recentMonth = month.sub(pitMonth, 1)

    return !datetime.isBefore(pit, getPublishTimeForMonth(recentMonth))
        ? recentMonth
        : month.sub(recentMonth,1)
}

export const MonthKPIApexMonthRangerFactory = <RenderSet>(
    {
        min = null,
        max = null,
        selected = null,
        renderer
    }: ApexMonthRangerOptions<RenderSet>
) => {
    const firstMonth = SHARE_PROFIT_DISTRIBUTION_EFFECTIVE_START_DATE
    let lastMonth = month.sub(month.getCurrentKapitelMonth(), 1)

    if (!useAuthStore().isImpersonating() || import.meta.env.VITE_ALLOW_IMPERSONATOR_MONTH_KPI_PREVIEW !== 'true') {
        lastMonth = month.min(getMostRecentPublishedMonth(), lastMonth)
    }

    if (!selected) {
        // default selection: last month
        selected = lastMonth
    }

    if (!max) {
        // allow lastMonth or two of the previous month in the default timeframe - after that, switch to -3 / +2
        max = date.isAfter(selected, month.sub(lastMonth, 2)) || date.isSameDay(selected, month.sub(lastMonth, 2))
            ? lastMonth
            : month.add(selected, 2)
    }

    if (!min) {
        // constrain lower border by firstMonth
        min = month.max(month.sub(max, MONTH_KPI_MONTH_BACK), firstMonth)
    }

    // check for max >= selected >= min >= firstMonth
    if (
        date.isAfter(firstMonth, min) ||
        date.isAfter(min, selected) ||
        date.isAfter(selected, max)
    ) {
        throw new Error("wrong config for month ranger: " + min + selected + max)
    }

    return new ApexMonthRanger({min, max, selected, renderer})
}
